/**
 * Stock Table Component
 */

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, Eye, Package } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { PermissionGate } from "@/components/PermissionGate";
import type { StockItem } from "@/types/stock";

interface StockTableProps {
  items: StockItem[];
  onViewHistory: (item: StockItem) => void;
  onViewProduct: (item: StockItem) => void;
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;
  onSort: (field: string) => void;
}


export function StockTable({ items, onViewHistory, onViewProduct, sortField, sortDirection, onSort }: StockTableProps) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center">
        <Package className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">Nenhum item em estoque</h3>
        <p className="text-muted-foreground">Finalize entradas de produtos para visualizar o estoque.</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort("produto_nome")}
            >
              Produto
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort("categoria")}
            >
              Categoria
            </TableHead>
            <TableHead
              className="text-right cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort("quantidade_atual")}
            >
              Quantidade
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-muted/50 transition-colors">
              Unid.
            </TableHead>
            <TableHead className="text-right cursor-pointer hover:bg-muted/50 transition-colors">
              Peso (g)
            </TableHead>
            <TableHead className="text-right cursor-pointer hover:bg-muted/50 transition-colors">
              Volume (ml)
            </TableHead>
            <TableHead
              className="text-right cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort("custo_medio_temporal")}
            >
              Custo Médio
            </TableHead>
            <TableHead
              className="text-right cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort("valor_total_estoque")}
            >
              Valor Estoque
            </TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="font-medium">{item.produto_nome}</TableCell>
              <TableCell>
                <Badge variant="outline">{item.categoria || "—"}</Badge>
              </TableCell>
              <TableCell className="text-right">
                <span className="font-mono">
                  {item.peso_total_g && item.peso_total_g > 0 
                    ? `${(item.peso_total_g / 1000).toFixed(2)} kg`
                    : item.volume_total_ml && item.volume_total_ml > 0
                    ? `${(item.volume_total_ml / 1000).toFixed(2)} L`
                    : `${Number(item.quantidade_atual).toFixed(0)} un`
                  }
                </span>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {item.tipo_embalagem || "—"}
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                {item.peso_total_g 
                  ? new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(Number(item.peso_total_g))
                  : "—"
                }
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                {item.volume_total_ml 
                  ? new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(Number(item.volume_total_ml))
                  : "—"
                }
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                {formatCurrency(Number(item.custo_medio_temporal))}
              </TableCell>
              <TableCell className="text-right font-mono font-semibold">
                {formatCurrency(Number(item.valor_total_estoque))}
              </TableCell>
              <TableCell>
                <div className="flex gap-2 justify-end">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-10 w-10 md:h-9 md:w-9 touch-manipulation"
                    onClick={() => onViewHistory(item)}
                    title="Ver histórico de movimentações"
                  >
                    <History className="w-5 h-5 md:w-4 md:h-4" />
                  </Button>
                  <PermissionGate resource="products" action="view">
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-10 w-10 md:h-9 md:w-9 touch-manipulation"
                      onClick={() => onViewProduct(item)} 
                      title="Visualizar produto"
                    >
                      <Eye className="w-5 h-5 md:w-4 md:h-4" />
                    </Button>
                  </PermissionGate>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
